import { useState, type ReactElement } from "react";
import {
  Text,
  Group,
  Stack,
  Paper,
  ScrollArea,
  Box,
  Divider,
  Menu,
  NumberInput,
} from "@mantine/core";
import {
  IconEye,
  IconEyeOff,
  IconGitBranch,
  IconCopy,
  IconTrash,
  IconPlus,
  IconX,
  IconClick,
} from "@tabler/icons-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input, ToggleItem, Button, Tooltip } from "@onengine/component-library";
import { useIsNarrowEditor } from "@/components/features/FormBuilder/hooks/useIsNarrowEditor";
import { ConfirmDeleteFieldModal } from "@/components/features/FormBuilder/components/QuestionCard/ConfirmDeleteFieldModal";
import { useFormStore, generateId } from "@/store/form-builder";
import type { QuestionScope } from "@/store/form-builder";
import {
  getSwitchableFieldTypes,
  FIELD_CATEGORIES,
} from "@/components/features/FormBuilder/data/fieldTypes";
import {
  BUILDER_LABELS,
  EMPTY_OPTION_PLACEHOLDER,
  FIELD_ACTION_LABELS,
  optionLabel,
} from "@/components/features/FormBuilder/data/builderLabels";
import {
  NO_PROVIDER_TYPE,
  buildProviderTypeContext,
} from "@/components/features/FormBuilder/data/contextTriggers";
import {
  tokensToLabels,
  labelsToTokens,
  getRowDisplay,
} from "@/components/features/FormBuilder/utils/renderRowTemplate";
import type { FormQuestion } from "@/components/features/FormBuilder/types/form.types";
import styles from "./RightPanel.module.scss";
import GroupedSelectInput from "@/components/ui/GroupedSelectInput/GroupedSelectInput";

function buildSampleItem(group: FormQuestion): Record<string, unknown> {
  const item: Record<string, unknown> = {};
  (group.fields || []).forEach((f) => {
    switch (f.type) {
      case "number":
        item[f.id] = 1;
        break;
      case "radio":
      case "dropdown":
      case "checkbox":
        item[f.id] = f.options?.[0]?.id ?? "";
        break;
      case "acknowledgement":
        item[f.id] = true;
        break;
      default:
        item[f.id] = f.question || f.typeName;
    }
  });
  return item;
}

function SortableRightPanelOption({
  option,
  index,
  questionType,
  scope,
  questionId,
  options,
}: {
  option: { id: string; value: string };
  index: number;
  questionType: string;
  scope: QuestionScope;
  questionId: string;
  options: { id: string; value: string }[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option.id,
  });
  const updateQuestion = useFormStore((s) => s.updateQuestion);

  return (
    <Group
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      gap={6}
      wrap="nowrap"
      className={styles.optionsInputContainer}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          cursor: "grab",
          padding: "0 2px",
          opacity: 0.4,
        }}
        {...attributes}
        {...listeners}
      >
        {[0, 1, 2].map((r) => (
          <Group key={r} gap={1}>
            <Box
              w={3}
              h={3}
              style={{ borderRadius: "50%", background: "var(--neutral-900,#646d7e)" }}
            />
            <Box
              w={3}
              h={3}
              style={{ borderRadius: "50%", background: "var(--neutral-900,#646d7e)" }}
            />
          </Group>
        ))}
      </Box>
      {questionType === "radio" && (
        <Box
          w={14}
          h={14}
          style={{
            borderRadius: "50%",
            border: "2px solid var(--border-default,#cfd4dd)",
            flexShrink: 0,
          }}
        />
      )}
      {/* {(questionType === "checkbox" || questionType === "dropdown") && (
        <Box w={14} h={14} style={{ borderRadius: 3, border: "2px solid #cfd4dd", flexShrink: 0 }} />
      )} */}
      <Box style={{ flex: 1 }}>
        <Input
          value={option.value}
          disabled={option.value === "Other"}
          onChange={(val) => {
            const o = [...options];
            o[index] = { ...o[index], value: val };
            updateQuestion(scope, questionId, { options: o });
          }}
          placeholder={EMPTY_OPTION_PLACEHOLDER}
          size="small"
          className={styles.optionsInput}
        />
      </Box>
      {options.length > 1 && (
        <Button
          type="icon"
          variant="tertiary"
          size="small"
          iconLeft={<IconX size={12} />}
          onClick={() => {
            const isOther = option.value === "Other";
            updateQuestion(scope, questionId, {
              options: options.filter((_, j) => j !== index),
              ...(isOther && { allowOther: false }),
            });
          }}
        />
      )}
    </Group>
  );
}

function RepeatingGroupSettingsPanel({
  question: q,
  upd,
}: {
  question: FormQuestion;
  upd: (u: Record<string, unknown>) => void;
}) {
  const groupConfig = q.groupConfig || { itemLabel: "Entry", addLabel: "Add another" };
  const fields = q.fields || [];
  const sample = buildSampleItem(q);
  const preview =
    fields.length > 0 ? getRowDisplay(q, sample, buildProviderTypeContext(NO_PROVIDER_TYPE)) : null;

  const updateGroupConfig = (u: Record<string, unknown>) =>
    upd({ groupConfig: { ...groupConfig, ...u } });

  const insertField = (templateKey: "rowTitle" | "rowSubtitle", field: FormQuestion) => {
    const current = tokensToLabels(groupConfig[templateKey] || "", q);
    const next = `${current}{${field.question || field.typeName}}`;
    updateGroupConfig({ [templateKey]: labelsToTokens(next, q) });
  };

  return (
    <Stack gap="sm" p="md" style={{ borderBottom: "1px solid var(--border-default,#cfd4dd)" }}>
      <Text size="sm" fw={"var(--font-weight-semi-bold,600)"} c="var(--text-default,#363d4d)">
        Repeating Group
      </Text>

      <Text size="xs" fw={"var(--font-weight-semi-bold,600)"} c="var(--text-strong,#131927)">
        Entry label
      </Text>
      <Input
        value={groupConfig.itemLabel}
        onChange={(val) => updateGroupConfig({ itemLabel: val })}
        placeholder="Entry"
        size="small"
      />

      <Text size="xs" fw={"var(--font-weight-semi-bold,600)"} c="var(--text-strong,#131927)">
        Add button label
      </Text>
      <Input
        value={groupConfig.addLabel}
        onChange={(val) => updateGroupConfig({ addLabel: val })}
        placeholder="Add another"
        size="small"
      />

      <Group gap="sm" grow>
        <Box>
          <Text size="xs" fw={"var(--font-weight-semi-bold,600)"} c="var(--text-strong,#131927)">
            Min entries
          </Text>
          <NumberInput
            value={groupConfig.minItems}
            min={0}
            size="sm"
            radius="md"
            onChange={(val) => {
              const min = typeof val === "number" ? val : undefined;
              const max =
                min !== undefined && groupConfig.maxItems !== undefined
                  ? Math.max(min, groupConfig.maxItems)
                  : groupConfig.maxItems;
              updateGroupConfig({ minItems: min, maxItems: max });
            }}
          />
        </Box>
        <Box>
          <Text size="xs" fw={"var(--font-weight-semi-bold,600)"} c="var(--text-strong,#131927)">
            Max entries
          </Text>
          <NumberInput
            value={groupConfig.maxItems}
            min={0}
            size="sm"
            radius="md"
            onChange={(val) => {
              const max = typeof val === "number" ? val : undefined;
              const min =
                max !== undefined && groupConfig.minItems !== undefined
                  ? Math.min(max, groupConfig.minItems)
                  : groupConfig.minItems;
              updateGroupConfig({ maxItems: max, minItems: min });
            }}
          />
        </Box>
      </Group>

      <Divider color="var(--neutral-500,#e2e6eb)" mt={4} />

      {(["rowTitle", "rowSubtitle"] as const).map((templateKey) => (
        <Stack gap={4} key={templateKey}>
          <Group justify="space-between" align="center">
            <Text size="xs" fw={"var(--font-weight-semi-bold,600)"} c="var(--text-strong,#131927)">
              {templateKey === "rowTitle" ? "Row title template" : "Row subtitle template"}
            </Text>
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <Button
                  type="icon"
                  variant="tertiary"
                  size="small"
                  isDisabled={fields.length === 0}
                  iconLeft={<IconPlus size={14} />}
                />
              </Menu.Target>
              <Menu.Dropdown>
                {fields.map((f) => (
                  <Menu.Item key={f.id} onClick={() => insertField(templateKey, f)}>
                    {f.question || f.typeName}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          </Group>
          <Input
            value={tokensToLabels(groupConfig[templateKey] || "", q)}
            onChange={(val) => updateGroupConfig({ [templateKey]: labelsToTokens(val, q) })}
            placeholder={
              templateKey === "rowTitle" ? "e.g. {Item name}" : "e.g. {Quantity} x {Price}"
            }
            size="small"
          />
        </Stack>
      ))}

      <Text size="xs" c="dimmed">
        Use {"{Field label}"} to insert an answer, {"{Quantity * Price}"} for simple maths, and
        {" ["}optional text{"]"} to hide a segment when a referenced field is empty.
      </Text>

      {preview && (preview.title || preview.subtitle) && (
        <Box p={8} style={{ background: "var(--bg-neutral-medium,#f0f2f5)", borderRadius: 8 }}>
          <Text size="xs" c="dimmed" mb={2}>
            Preview
          </Text>
          <Text size="sm" fw={600} c="var(--text-default,#363d4d)">
            {preview.title}
          </Text>
          {preview.subtitle && (
            <Text size="xs" c="dimmed">
              {preview.subtitle}
            </Text>
          )}
        </Box>
      )}
    </Stack>
  );
}

function useSelectedQuestionDepth(
  questionId: string | null,
  scope: QuestionScope | undefined,
): number {
  const { getScopeQuestions, getConditionalChildIds } = useFormStore();
  if (!questionId || !scope) return 0;
  const scopeQs = getScopeQuestions(scope);
  const parentOf: Record<string, string> = {};
  for (const q of scopeQs) {
    const kids = getConditionalChildIds(scopeQs, q.id);
    for (const kid of kids) parentOf[kid] = q.id;
  }
  let depth = 0;
  let cur = questionId;
  const seen = new Set<string>([questionId]);
  while (parentOf[cur] && !seen.has(parentOf[cur])) {
    depth++;
    cur = parentOf[cur];
    seen.add(cur);
  }
  return depth;
}

interface FieldActionProps {
  labelled: boolean;
  label: string;
  text: string;
  icon: ReactElement;
  onClick: () => void;
  pressed?: boolean;
  active?: boolean;
}

// A finger has no hover to reveal a tooltip, so on a phone each action carries its own word.
function FieldAction({ labelled, label, text, icon, onClick, pressed, active }: FieldActionProps) {
  const htmlAttributes = {
    "aria-label": label,
    ...(pressed === undefined ? {} : { "aria-pressed": pressed }),
  };
  if (labelled) {
    return (
      <Button
        variant={active ? "secondary" : "tertiary"}
        size="small"
        iconLeft={icon}
        className={styles.labelledAction}
        htmlAttributes={htmlAttributes}
        onClick={onClick}
      >
        {text}
      </Button>
    );
  }
  return (
    <Tooltip content={label}>
      <Button
        type="icon"
        variant={active ? "secondary" : "tertiary"}
        className={active ? styles.conditionButtonEnabled : ""}
        size="small"
        iconLeft={icon}
        htmlAttributes={htmlAttributes}
        onClick={onClick}
      />
    </Tooltip>
  );
}

export function RightPanel() {
  const {
    selectedQuestionId,
    findQuestion,
    updateQuestion,
    deleteQuestion,
    duplicateQuestion,
    reorderOptions,
  } = useFormStore();
  const [activeOptId, setActiveOptId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const isNarrow = useIsNarrowEditor();
  const optSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );
  const found = selectedQuestionId ? findQuestion(selectedQuestionId) : null;
  const q = found?.question;
  const scope = found?.scope;
  const pid = scope?.pageId;
  const sid = scope?.sectionId;
  const selectedDepth = useSelectedQuestionDepth(selectedQuestionId, scope);

  if (!q || !pid || !sid) {
    return (
      <Paper
        w="100%"
        style={{
          flexShrink: 0,
          borderLeft: "1px solid var(--border-default,#cfd4dd)",
          borderRadius: 0,
        }}
        bg="var(--bg-neutral-disabled,#f8f8fa)"
        h="100%"
      >
        <Stack align="center" justify="center" h="100%" p="xl">
          <Box
            w={48}
            h={48}
            style={{
              borderRadius: "50%",
              background: "var(--bg-surface-base,#e2e6eb)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconClick size={26} color="var(--neutral-900,#646d7e)" />
          </Box>
          <Text size="sm" c="dimmed" ta="center" lh={1.5} fw={600}>
            No field selected. Click on a form field to edit its properties.
          </Text>
        </Stack>
      </Paper>
    );
  }

  const upd = (u: Record<string, unknown>) => updateQuestion(scope!, q.id, u);
  const hasPH = ![
    "date",
    "time",
    "date-time",
    "photo-upload",
    "video-upload",
    "radio",
    "checkbox",
    "dropdown",
    "acknowledgement",
  ].includes(q.type);
  const hasOpts = ["radio", "checkbox", "dropdown"].includes(q.type);

  return (
    <ScrollArea
      w="100%"
      h="100%"
      style={{ flexShrink: 0, borderLeft: "1px solid var(--border-default,#cfd4dd)" }}
      bg="var(--bg-neutral-disabled,#f8f8fa)"
    >
      {/* Field Actions */}
      <Stack gap="sm" p="md" style={{ borderBottom: "1px solid var(--border-default,#cfd4dd)" }}>
        <Text size="sm" fw={"var(--font-weight-semi-bold,600)"} c="var(--text-default,#363d4d)">
          Field Actions
        </Text>
        <Text
          size="xs"
          fw={"var(--font-weight-semi-bold,600)"}
          c="var(--text-strong,#131927)"
          mt={4}
        >
          Field Type
        </Text>
        {q.type === "repeating-group" ? (
          <Text size="sm" fw={500} c="var(--text-default,#363d4d)">
            Repeating Group
          </Text>
        ) : (
          <GroupedSelectInput
            value={q.type}
            onChange={(v) => {
              if (!v) return;
              const ft = getSwitchableFieldTypes(scope).find((f) => f.id === v);
              if (ft) upd({ type: ft.id, typeName: ft.label });
            }}
            groups={FIELD_CATEGORIES.map((cat) => ({
              label: cat,
              items: getSwitchableFieldTypes(scope)
                .filter((f) => f.category === cat)
                .map((f) => ({
                  value: f.id,
                  label: f.label,
                  icon: <f.icon size={16} />,
                  disabled: !!f.comingSoon,
                })),
            }))}
          />
        )}

        <Group gap={isNarrow ? "xs" : 2} mt={4}>
          <FieldAction
            labelled={isNarrow}
            label={FIELD_ACTION_LABELS.visibility}
            text="Visibility"
            icon={q.hidden ? <IconEyeOff size={16} /> : <IconEye size={16} />}
            pressed={!!q.hidden}
            onClick={() => upd({ hidden: !q.hidden })}
          />
          {selectedDepth < 3 && (
            <FieldAction
              labelled={isNarrow}
              label={FIELD_ACTION_LABELS.conditionalLogic}
              text="Logic"
              icon={<IconGitBranch size={16} />}
              pressed={!!q.conditionalLogicEnabled}
              active={!!q.conditionalLogicEnabled}
              onClick={() => upd({ conditionalLogicEnabled: !q.conditionalLogicEnabled })}
            />
          )}
          <FieldAction
            labelled={isNarrow}
            label={FIELD_ACTION_LABELS.duplicate}
            text="Duplicate"
            icon={<IconCopy size={16} />}
            onClick={() => duplicateQuestion(scope!, q.id)}
          />
          <FieldAction
            labelled={isNarrow}
            label={FIELD_ACTION_LABELS.delete}
            text="Delete"
            icon={<IconTrash size={16} />}
            onClick={() => setDeleteConfirmOpen(true)}
          />
        </Group>
        <ConfirmDeleteFieldModal
          isOpen={deleteConfirmOpen}
          fieldName={q.question}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={() => {
            setDeleteConfirmOpen(false);
            deleteQuestion(scope!, q.id);
          }}
        />
      </Stack>

      {q.type === "repeating-group" && <RepeatingGroupSettingsPanel question={q} upd={upd} />}

      {/* Basics */}
      <Stack gap="sm" p="md" style={{ borderBottom: "1px solid var(--border-default,#cfd4dd)" }}>
        <Text size="sm" fw={"var(--font-weight-semi-bold,600)"} c="var(--text-default,#363d4d)">
          Basics
        </Text>
        <Text
          size="xs"
          fw={"var(--font-weight-semi-bold,600)"}
          c="var(--text-strong,#131927)"
          mt={4}
        >
          Question
        </Text>
        <Input
          value={q.question}
          onChange={(val) => upd({ question: val })}
          placeholder={BUILDER_LABELS.emptyQuestionPlaceholder}
          size="small"
        />
        {hasPH && (
          <>
            <Text
              size="xs"
              fw={"var(--font-weight-semi-bold,600)"}
              c="var(--text-strong,#131927)"
              mt={4}
            >
              Placeholder
            </Text>
            <Input
              value={q.placeholder || ""}
              onChange={(val) => upd({ placeholder: val })}
              placeholder="Placeholder text..."
              size="small"
            />
          </>
        )}
        {q.type === "number" && (
          <>
            <Text
              size="xs"
              fw={"var(--font-weight-semi-bold,600)"}
              c="var(--text-strong,#131927)"
              mt={4}
            >
              Unit
            </Text>
            <Input
              value={q.unit || ""}
              onChange={(val) => upd({ unit: val })}
              placeholder="e.g. kg, cm, $"
              size="small"
            />
          </>
        )}
        {hasOpts && (
          <>
            <Text
              size="xs"
              fw={"var(--font-weight-semi-bold,600)"}
              c="var(--text-strong,#131927)"
              mt={4}
            >
              Options
            </Text>
            <DndContext
              sensors={optSensors}
              collisionDetection={closestCenter}
              onDragStart={(e: DragStartEvent) => setActiveOptId(e.active.id as string)}
              onDragEnd={(e: DragEndEvent) => {
                setActiveOptId(null);
                const { active, over } = e;
                if (!over || active.id === over.id) return;
                const opts = q.options || [];
                const oldIdx = opts.findIndex((o) => o.id === active.id);
                const newIdx = opts.findIndex((o) => o.id === over.id);
                if (oldIdx >= 0 && newIdx >= 0) reorderOptions(scope!, q.id, oldIdx, newIdx);
              }}
            >
              <SortableContext
                items={(q.options || []).filter((o) => o.value !== "Other").map((o) => o.id)}
                strategy={verticalListSortingStrategy}
              >
                {(q.options || [])
                  .filter((opt) => opt.value !== "Other")
                  .map((opt) => {
                    const realIndex = (q.options || []).findIndex((o) => o.id === opt.id);
                    return (
                      <SortableRightPanelOption
                        key={opt.id}
                        option={opt}
                        index={realIndex}
                        questionType={q.type}
                        scope={scope!}
                        questionId={q.id}
                        options={q.options || []}
                      />
                    );
                  })}
              </SortableContext>
              <DragOverlay dropAnimation={{ duration: 150, easing: "ease" }}>
                {activeOptId &&
                  (() => {
                    const opt = (q.options || []).find((o) => o.id === activeOptId);
                    if (!opt) return null;
                    return (
                      <Paper p="xs" radius="md" withBorder shadow="sm">
                        <Text size="sm">{optionLabel(opt.value)}</Text>
                      </Paper>
                    );
                  })()}
              </DragOverlay>
            </DndContext>
            {q.allowOther && (
              <Group
                gap={6}
                wrap="nowrap"
                justify="space-between"
                mt={2}
                className={styles.otherOptionContainer}
              >
                {q.type === "radio" && <Box w={14} h={14} className={styles.radioIndicator} />}
                {q.type === "checkbox" && (
                  <Box w={14} h={14} className={styles.checkboxIndicator} />
                )}
                <Box className={styles.otherOptionLabel}>
                  <Text size="sm" c="dimmed" fs="italic">
                    Other (text input)
                  </Text>
                </Box>
                <Button
                  type="icon"
                  variant="tertiary"
                  size="small"
                  iconLeft={<IconX size={12} />}
                  onClick={() =>
                    upd({
                      allowOther: false,
                      options: (q.options || []).filter((o) => o.value !== "Other"),
                    })
                  }
                />
              </Group>
            )}
            <Divider color="var(--neutral-500,#e2e6eb)" mt={4} />
            <Group gap={6} mt={2}>
              <Text
                component="button"
                size="var(--text-font-size-medium, 14px)"
                fw={500}
                c="var(--primary-default,#004ddb)"
                onClick={() => {
                  const opts = q.options || [];
                  const otherIdx = opts.findIndex((o) => o.value === "Other");
                  const newOpt = { id: generateId("o"), value: "" };
                  const updated =
                    otherIdx !== -1
                      ? [...opts.slice(0, otherIdx), newOpt, ...opts.slice(otherIdx)]
                      : [...opts, newOpt];
                  upd({ options: updated });
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <IconPlus size={14} /> New option
              </Text>
              {!q.allowOther && (
                <>
                  <Text size="sm" c="dimmed">
                    or
                  </Text>
                  <Text
                    component="button"
                    size="var(--text-font-size-medium, 14px)"
                    fw={500}
                    c="var(--primary-default,#004ddb)"
                    onClick={() =>
                      upd({
                        options: [...(q.options || []), { id: generateId("o"), value: "Other" }],
                        allowOther: true,
                      })
                    }
                    style={{ border: "none", background: "transparent", cursor: "pointer" }}
                  >
                    Add &quot;Other&quot;
                  </Text>
                  <Text size="sm" c="dimmed">
                    option
                  </Text>
                </>
              )}
            </Group>

            {/* <ToggleItem
              checked={q.allowOther || false}
              onChange={(next) => {
                if (next) {
                  upd({
                    allowOther: true,
                    options: [...(q.options || []), { id: generateId("o"), value: "Other" }],
                  });
                } else {
                  upd({
                    allowOther: false,
                    options: (q.options || []).filter((o) => o.value !== "Other"),
                  });
                }
              }}
              size="lg"
              intent="brand"
              primary="Allow 'Other'"
            /> */}
          </>
        )}
        <ToggleItem
          checked={!!q.description}
          onChange={(next) => upd({ description: next ? " " : "" })}
          size="lg"
          intent="brand"
          primary="Add help text"
        />
        {q.description && (
          <Input
            value={q.description === " " ? "" : q.description}
            onChange={(val) => upd({ description: val || " " })}
            placeholder="Enter description..."
            size="small"
          />
        )}
      </Stack>

      {/* Validation */}
      <Stack gap="sm" p="md" style={{ borderBottom: "1px solid var(--border-default,#cfd4dd)" }}>
        <Text size="sm" fw={"var(--font-weight-semi-bold,600)"} c="var(--text-default,#363d4d)">
          Field Validation
        </Text>
        {q.type === "dropdown" && (
          <ToggleItem
            checked={q.allowMultiple || false}
            onChange={(next) => upd({ allowMultiple: next })}
            size="lg"
            intent="brand"
            primary="Allow multiple selections"
          />
        )}

        {q.type === "photo-upload" && (
          <>
            <Group gap={8}>
              <Text
                size="xs"
                fw={"var(--font-weight-semi-bold,600)"}
                c="var(--text-strong,#131927)"
              >
                Required Photos:
              </Text>
              <Group gap={4}>
                <Button
                  type="icon"
                  variant="neutral-secondary"
                  size="small"
                  onClick={() => {
                    const s = q.photoSlots || [{ id: "1", label: "" }];
                    if (s.length > 1) upd({ photoSlots: s.slice(0, -1) });
                  }}
                >
                  <Text size="sm" fw={"var(--font-weight-bold,700)"}>
                    −
                  </Text>
                </Button>
                <Text size="sm" fw={"var(--font-weight-semi-bold,600)"} w={24} ta="center">
                  {q.photoSlots?.length || 1}
                </Text>
                <Button
                  type="icon"
                  variant="neutral-secondary"
                  size="small"
                  iconLeft={<IconPlus size={12} />}
                  onClick={() => {
                    const s = q.photoSlots || [{ id: "1", label: "" }];
                    upd({ photoSlots: [...s, { id: `${Date.now()}`, label: "" }] });
                  }}
                />
              </Group>
            </Group>
            {(q.photoSlots || [{ id: "1", label: "" }]).map((slot, si) => (
              <Group key={slot.id} gap={6}>
                <Box
                  w={22}
                  h={22}
                  style={{
                    borderRadius: "50%",
                    background: "var(--bg-neutral-medium,#f0f2f5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#646d7e",
                    flexShrink: 0,
                  }}
                >
                  {si + 1}
                </Box>
                <Box style={{ flex: 1 }}>
                  <Input
                    value={slot.label}
                    placeholder="e.g. Front of ID, Receipt photo..."
                    size="small"
                    onChange={(val) => {
                      const s = [...(q.photoSlots || [])];
                      s[si] = { ...s[si], label: val };
                      upd({ photoSlots: s });
                    }}
                  />
                </Box>
              </Group>
            ))}
            <ToggleItem
              checked={q.allowAdditionalPhotos || false}
              onChange={(next) => upd({ allowAdditionalPhotos: next })}
              size="lg"
              intent="brand"
              primary="Allow additional photos"
            />
          </>
        )}

        {q.type === "video-upload" && (
          <>
            <Group gap={8}>
              <Text
                size="xs"
                fw={"var(--font-weight-semi-bold,600)"}
                c="var(--text-strong,#131927)"
              >
                Required Videos:
              </Text>
              <Group gap={4}>
                <Button
                  type="icon"
                  variant="neutral-secondary"
                  size="small"
                  onClick={() => {
                    const s = q.videoSlots || [{ id: "1", label: "" }];
                    if (s.length > 1) upd({ videoSlots: s.slice(0, -1) });
                  }}
                >
                  <Text size="sm" fw={"var(--font-weight-bold,700)"}>
                    −
                  </Text>
                </Button>
                <Text size="sm" fw={"var(--font-weight-semi-bold,600)"} w={24} ta="center">
                  {q.videoSlots?.length || 1}
                </Text>
                <Button
                  type="icon"
                  variant="neutral-secondary"
                  size="small"
                  iconLeft={<IconPlus size={12} />}
                  onClick={() => {
                    const s = q.videoSlots || [{ id: "1", label: "" }];
                    upd({ videoSlots: [...s, { id: `${Date.now()}`, label: "" }] });
                  }}
                />
              </Group>
            </Group>
            {(q.videoSlots || [{ id: "1", label: "" }]).map((slot, si) => (
              <Group key={slot.id} gap={6}>
                <Box
                  w={22}
                  h={22}
                  style={{
                    borderRadius: "50%",
                    background: "var(--bg-neutral-medium,#f0f2f5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#646d7e",
                    flexShrink: 0,
                  }}
                >
                  {si + 1}
                </Box>
                <Box style={{ flex: 1 }}>
                  <Input
                    value={slot.label}
                    placeholder="e.g. Product demo, Walk-through..."
                    size="small"
                    onChange={(val) => {
                      const s = [...(q.videoSlots || [])];
                      s[si] = { ...s[si], label: val };
                      upd({ videoSlots: s });
                    }}
                  />
                </Box>
              </Group>
            ))}
            <ToggleItem
              checked={q.allowAdditionalVideos || false}
              onChange={(next) => upd({ allowAdditionalVideos: next })}
              size="lg"
              intent="brand"
              primary="Allow additional videos"
            />
          </>
        )}

        <ToggleItem
          checked={!q.required}
          onChange={(next) => upd({ required: !next })}
          size="lg"
          intent="brand"
          primary="Mark as optional"
        />
      </Stack>
    </ScrollArea>
  );
}
